import { t } from "@/i18n-js/instance";
import { OpenQPModal } from "@/react/components/QuickPost/OpenQPModal";
import { useConfirmModal } from "@circle-react/components/LiveStreams/Room/hooks";
import type { Attachment } from "@circle-react/types/Attachment";
import type { Room } from "@circle-react/types/Live/Room";
import { Icon } from "@circle-react-shared/Icon";
import { Tippy } from "@circle-react-shared/Tippy";
import { IconDropdownMenu } from "@circle-react-uikit/IconDropdownMenu";
import { TranscriptsPlayer } from "@circle-react-uikit/TranscriptsPlayer";
import { useDeleteRecording } from "../../../Actions/hooks/useDeleteRecording";
import { usePostBodyWithRecording } from "../../../Actions/hooks/usePostBodyWithRecording";

interface RecordingProps {
  recording: Attachment;
  refetch: () => void;
  room: Room;
}

export const Recording = ({ room, recording, refetch }: RecordingProps) => {
  const { deleteRecording } = useDeleteRecording({ room, refetch });
  const { trixBodyWithRecording, tiptapBodyWithRecording } =
    usePostBodyWithRecording({ room });
  const {
    confirmModal: deleteRecordingModal,
    onConfirmButtonClick: onDeleteRecordingButtonClick,
  } = useConfirmModal({
    title: t("live_streams.delete_recording.confirm"),
    onConfirm: deleteRecording,
    description: t("live_streams.delete_recording.confirm_description"),
    maxIndex: true,
  });
  const menuItems = [
    {
      key: "download",
      label: t("live_streams.dashboard.details.download"),
      href: room.recording_url,
    },
    {
      key: "delete",
      label: t("live_streams.dashboard.details.delete"),
      onClick: onDeleteRecordingButtonClick,
    },
    {
      key: "post-to-space",
      linkComponent: (
        <OpenQPModal
          hasIcon={false}
          isButton={false}
          linkName={t("live_streams.dashboard.table.actions.post_to_space")}
          bodyForEditor={trixBodyWithRecording}
          tiptapBodyForEditor={tiptapBodyWithRecording as object}
          title={room.name}
          maxZIndexEnabled
        />
      ),
      label: t("live_streams.dashboard.table.actions.post_to_space"),
    },
  ];
  if (room.transcript_url) {
    const onDownloadTranscriptClick = () => {
      window.open(room.transcript_url || "", "_blank");
    };
    menuItems.splice(1, 0, {
      key: "download-transcript",
      label: t("live_streams.dashboard.details.download_transcript"),
      onClick: onDownloadTranscriptClick,
    });
  }

  return (
    <div>
      <TranscriptsPlayer
        src={recording.url}
        type={recording.content_type}
        mediaTranscoding={recording.media_transcoding}
        mediaTranscriptId={recording.media_transcript_id}
        isDownloadable={recording.is_downloadable}
        poster={recording.image_variants?.thumbnail}
        downloadUrl={recording.url}
        chaptersUrl={recording.chapters_url}
        previewThumbnailsUrl={recording.preview_thumbnails_url}
        variants={recording.video_variants}
        signedId={recording.signed_id}
      />
      <div className="flex items-center justify-between">
        <div className="text-dark mb-1 mt-4 text-lg font-semibold">
          {room.recording_name}
        </div>
        {deleteRecordingModal}
        <IconDropdownMenu
          renderButton={() => (
            <Tippy content={t("live_streams.dashboard.details.actions")}>
              <Icon type="menu-dots-vertical" />
            </Tippy>
          )}
          menuItems={menuItems}
          dropdownSide="left"
          shouldCloseOnClickItem
        />
      </div>
    </div>
  );
};
